import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/entreprise/Chauffeur-pour-entreprise.png";

const sophiaAntipolis: ChauffeurPage = {
  slug: "sophia-antipolis",
  title: "VTC & chauffeur privé à Sophia Antipolis",
  subtitle:
    "Chauffeur d'affaires à Sophia Antipolis : transferts cadres, réunions multi-sites et navettes de conférence dans la technopole.",
  keywords: [
    "vtc sophia antipolis",
    "chauffeur privé sophia antipolis",
    "chauffeur affaires sophia antipolis",
  ],
  hero: {
    title: "VTC & chauffeur privé à Sophia Antipolis",
    subtitle:
      "La première technopole d'Europe : un chauffeur d'affaires pour cadres, délégations et conférences.",
    keywords: [
      "VTC Sophia Antipolis",
      "chauffeur affaires Sophia Antipolis",
      "navette conférence technopole",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & chauffeur privé à Sophia Antipolis",
    badges: [
      "Transferts cadres",
      "Réunions multi-sites",
      "Facturation entreprise",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Sophia Antipolis",
    items: [
      {
        title: "Transferts exécutifs",
        description:
          "Accueil de dirigeants et de cadres à l'aéroport de Nice, suivi de vol et prise en charge directe vers la technopole.",
      },
      {
        title: "Technopole étendue",
        description:
          "Sièges, centres de R&D et parcs d'activités dispersés sur un vaste plateau boisé : un chauffeur qui en connaît chaque adresse.",
      },
      {
        title: "Réunions multi-sites",
        description:
          "Enchaînement de rendez-vous sur la journée entre Sophia, Antibes, Cannes et Nice, sans perte de temps ni stationnement.",
      },
      {
        title: "Cadre professionnel",
        description:
          "Discrétion, ponctualité et facturation entreprise pour des déplacements business sans friction.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur à Sophia Antipolis",
    services: [
      {
        title: "Transferts aéroport exécutifs",
        text: "Sophia Antipolis ↔ aéroport de Nice avec suivi de vol et accueil pancarte, pour dirigeants, cadres et délégations.",
      },
      {
        title: "Réunions multi-sites",
        text: "Chauffeur à disposition pour enchaîner les rendez-vous entre les sites de la technopole, Antibes, Cannes et Nice.",
      },
      {
        title: "Navettes de conférence",
        text: "Acheminement d'intervenants et de participants entre hôtels, centres de congrès et lieux de séminaire, coordination groupes.",
      },
      {
        title: "Chauffeur à disposition, demi-journée ou journée",
        text: "Véhicule et chauffeur dédiés au rythme de votre agenda : 320 € la demi-journée de 4 h en Classe E, 480 € en Classe V (7 places), 600 € en Classe S. Journée de 8 h de 600 € à 1 100 € selon la classe.",
      },
      {
        title: "Roadshows & délégations",
        text: "Itinéraires planifiés pour visites de sites, investisseurs et partenaires, avec facturation entreprise claire.",
      },
    ],
  },
  locations: {
    heading: "Sites & points de prise en charge",
    items: [
      {
        name: "Cœur de la technopole",
        description:
          "Sièges, R&D et parcs d'activités sur le plateau de Valbonne.",
      },
      {
        name: "Hôtels d'affaires",
        description:
          "Établissements de séminaire et résidences cadres du secteur.",
      },
      {
        name: "Centres de congrès & séminaire",
        description:
          "Lieux de conférence et espaces événementiels de la technopole.",
      },
      {
        name: "Valbonne",
        description:
          "Village et abords résidentiels jouxtant le parc technologique.",
      },
      {
        name: "Antibes",
        description:
          "Ville voisine, gare et hôtels à quelques minutes du parc.",
      },
      {
        name: "Aéroport de Nice",
        description: "Environ 25 minutes par l'A8, accueil aux deux terminaux.",
      },
    ],
  },
  generic: {
    heading: "Sophia Antipolis, la technopole d'affaires de la Riviera",
    subheading:
      "Le plus grand parc technologique d'Europe, où les déplacements business exigent fiabilité et ponctualité.",
    paragraphs: [
      "Sophia Antipolis rassemble sur un vaste plateau boisé, entre Antibes et Valbonne, sièges sociaux, centres de R&D et parcs d'activités. Les sites y sont dispersés et mal desservis sans véhicule : un chauffeur qui en connaît chaque adresse fait gagner un temps précieux aux cadres et aux délégations en visite.",
      "À environ 25 minutes de l'aéroport de Nice par l'A8, la technopole est un point d'arrivée fréquent pour les voyageurs d'affaires. Accueil pancarte, suivi de vol et prise en charge directe vers le bureau ou l'hôtel : le transfert exécutif démarre la journée sans accroc.",
      "Conférences, séminaires et roadshows ponctuent la vie de Sophia. Pour ces formats, nous assurons des navettes coordonnées entre hôtels, centres de congrès et sièges, ainsi qu'une mise à disposition à la journée pour enchaîner les réunions multi-sites jusqu'à Cannes ou Nice. La facturation entreprise est claire et adaptée.",
      "Nos tarifs sont publics, ce qui reste rare sur ce marché. La mise à disposition démarre à 240 € pour 3 h en Classe E et 450 € en Classe S ; une demi-journée de 4 h coûte 320 € en Classe E, 480 € en Classe V et 600 € en Classe S. Au-delà du forfait, l'heure supplémentaire est facturée de 80 € à 150 € selon la classe. Le transfert Sophia Antipolis ⇄ aéroport de Nice suit la grille forfaitaire de la Riviera, et tout trajet hors forfait est estimé à partir de 2,60 €/km.",
    ],
    bullets: [
      "Mise à disposition dès 240 € (3 h) et 320 € la demi-journée",
      "Suivi de vol cadres, accueil pancarte aux deux terminaux",
      "Réunions multi-sites Sophia · Antibes · Cannes · Nice",
      "Facturation entreprise, entreprise déclarée depuis 2022",
    ],
  },
  process: {
    heading: "Réserver un chauffeur à Sophia Antipolis",
    steps: [
      "Transmettez votre programme (vol, sites à visiter, conférence, horaires de réunion).",
      "Nous planifions l'itinéraire et la mise à disposition selon votre agenda business.",
      "Le chauffeur accueille à l'aéroport ou se présente au site indiqué.",
      "Transferts directs ou journée complète multi-sites, avec facturation entreprise.",
    ],
  },
  faq: [
    {
      q: "Combien de temps de l'aéroport de Nice à Sophia Antipolis ?",
      a: "Comptez environ 25 minutes par l'A8 hors heures de pointe. Nous suivons le vol des cadres et délégations pour ajuster la prise en charge et tenir l'agenda de la journée.",
    },
    {
      q: "Pouvez-vous gérer des réunions sur plusieurs sites dans la journée ?",
      a: "Oui. La mise à disposition d'un chauffeur permet d'enchaîner les rendez-vous entre les sites de la technopole, Antibes, Cannes et Nice, sans perte de temps ni recherche de stationnement.",
    },
    {
      q: "Proposez-vous des navettes pour les conférences et séminaires ?",
      a: "Oui, nous acheminons intervenants et participants entre hôtels, centres de congrès et lieux de séminaire, avec coordination de groupe et horaires calés sur le programme.",
    },
    {
      q: "La facturation entreprise est-elle possible ?",
      a: "Oui. Nous proposons une facturation entreprise claire, adaptée aux déplacements professionnels récurrents, aux transferts cadres et aux prestations événementielles de la technopole.",
    },
    {
      q: "Combien coûte un chauffeur à disposition à Sophia Antipolis ?",
      a: "La mise à disposition démarre à 240 € pour 3 h en Classe E, 360 € en Classe V (7 places) et 450 € en Classe S. Une demi-journée de 4 h revient à 320 €, 480 € ou 600 € selon la même progression, et une journée de 8 h va de 600 € à 1 100 €. La durée minimum est de 3 h ; au-delà du forfait retenu, l'heure supplémentaire coûte de 80 € à 150 € selon la classe.",
    },
    {
      q: "Assurez-vous la liaison Nice ⇄ Sophia Antipolis ?",
      a: "Oui, c'est l'un de nos axes les plus demandés : environ 25 minutes par l'A8 depuis l'aéroport de Nice, et une trentaine depuis le centre de Nice. Le trajet peut être réservé en transfert simple ou intégré à une mise à disposition si plusieurs rendez-vous s'enchaînent dans la journée.",
    },
  ],
  relatedLinks: [
    {
      label: "Grille des mises à disposition",
      href: "/chauffeur#mise-a-disposition",
    },
    { label: "Chauffeur d'entreprise", href: "/chauffeur/entreprise" },
    {
      label: "Mise à disposition avec chauffeur à Nice",
      href: "/chauffeur/nice",
    },
    {
      label: "Chauffeur privé Cannes Aéroport de Nice",
      href: "/chauffeur/transferts/cannes-aeroport-nice",
    },
    {
      label: "Transferts, tarifs et durées",
      href: "/chauffeur/transferts",
    },
  ],
  cta: {
    title: "Votre chauffeur d'affaires à Sophia Antipolis",
    text: "Transfert cadre, réunions multi-sites ou navette de conférence : réservez votre chauffeur dans la technopole.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  // Titre recalé sur le cluster réel de Search Console : « chauffeur
  // entreprise/corporate sophia antipolis » sont les mieux positionnés
  // (~9e), « chauffeur privé » et « vtc » les plus volumineux. 385
  // impressions sur la page, aucun clic — l'ancien titre ne reprenait ni
  // « privé » ni « entreprise ». Description raccourcie sous 160 caractères
  // et porteuse d'un prix, seul différenciateur face à des concurrents qui
  // n'en affichent aucun.
  seo: {
    title: "Chauffeur privé & VTC d'entreprise à Sophia Antipolis",
    description:
      "Chauffeur privé et VTC à Sophia Antipolis pour cadres et entreprises : mise à disposition dès 240 €, transferts aéroport de Nice, facturation entreprise.",
    canonical: "/chauffeur/sophia-antipolis",
  },
};

export default sophiaAntipolis;
